import { useEffect, useRef, useState } from 'react';

/**
 * Chapter 005 · THE STUDIO
 * ────────────────────────────────────────────
 * Interactions:
 *  • Live Kanazawa (JST) clock — updates every second
 *  • Draggable 11-day production timeline: click anywhere on the
 *    track or drag the handle. The active step & photograph update
 *    in real-time as you scrub.
 *  • Full-bleed marquee at the bottom, pause on hover
 */

type Step = {
  index: string;
  dayRange: [number, number]; // 1..11
  day: string;
  title: string;
  body: string;
  image: string;
};

const STEPS: Step[] = [
  {
    index: '01',
    dayRange: [1, 3],
    day: 'DAY 01 – 03',
    title: 'Rough milling',
    body: 'From billet to shape, within 0.05mm.',
    image:
      'https://readdy.ai/api/search-image?query=Ultra%20photorealistic%20hyperreal%20close%20up%20detail%20of%20raw%20titanium%20metal%20billet%20block%20being%20rough%20milled%20on%20a%20five%20axis%20CNC%20machine%2C%20tiny%20metal%20shavings%20glinting%20under%20cool%20blue%20workshop%20light%2C%20precision%20industrial%20engineering%20scene%2C%20deep%20navy%20blue%20charcoal%20machine%20background%2C%20cinematic%20cool%20blue%20key%20light%20from%20upper%20left%20with%20subtle%20cyan%20rim%20light%2C%20moody%20dark%20editorial%20industrial%20photography%2C%20shallow%20depth%20of%20field%2C%20high%20detail%20brushed%20titanium%20surface%20microtexture%2C%20fine%20airborne%20dust%20particles%20catching%20the%20blue%20light%2C%20no%20people%20visible%2C%20shot%20on%20Hasselblad%20medium%20format%20with%20100mm%20macro%20lens%20at%20f4%20ISO%20200%2C%20razor%20sharp%20focus%2C%20lifelike%20realism&width=2000&height=1200&seq=axiom-studio-step-01-v3-blue&orientation=landscape',
  },
  {
    index: '02',
    dayRange: [4, 6],
    day: 'DAY 04 – 06',
    title: 'Finish milling',
    body: 'Diamond tool. Tolerance to 0.02mm.',
    image:
      'https://readdy.ai/api/search-image?query=Ultra%20photorealistic%20hyperreal%20close%20up%20macro%20of%20a%20diamond%20tipped%20cutting%20tool%20finishing%20the%20mirror%20polished%20surface%20of%20a%20titanium%20audio%20chassis%2C%20extreme%20precision%20engineering%20detail%2C%20deep%20navy%20blue%20charcoal%20machine%20background%2C%20cinematic%20cool%20blue%20key%20light%20from%20upper%20left%20with%20subtle%20cyan%20rim%20highlights%20on%20the%20mirror%20metal%2C%20moody%20dark%20editorial%20industrial%20photography%2C%20shallow%20depth%20of%20field%2C%20visible%20microscopic%20tool%20marks%20on%20titanium%20mirror%20surface%2C%20fine%20airborne%20metal%20dust%20catching%20the%20blue%20light%2C%20no%20people%20visible%2C%20shot%20on%20Hasselblad%20medium%20format%20with%20120mm%20macro%20lens%20at%20f5.6%20ISO%20200%2C%20razor%20sharp%20focus%2C%20lifelike%20realism&width=2000&height=1200&seq=axiom-studio-step-02-v3-blue&orientation=landscape',
  },
  {
    index: '03',
    dayRange: [7, 9],
    day: 'DAY 07 – 09',
    title: 'Hand assembly',
    body: 'Forty-four parts. One technician. Signed.',
    image:
      'https://readdy.ai/api/search-image?query=Ultra%20photorealistic%20hyperreal%20close%20up%20of%20white%20gloved%20hands%20using%20precision%20tweezers%20to%20assemble%20tiny%20audio%20components%20inside%20a%20polished%20titanium%20chassis%20on%20a%20dark%20matte%20metal%20workbench%2C%20quiet%20Japanese%20precision%20workshop%20atmosphere%2C%20deep%20navy%20blue%20charcoal%20surroundings%2C%20cinematic%20cool%20blue%20key%20light%20from%20upper%20left%20with%20subtle%20cyan%20fill%2C%20moody%20dark%20editorial%20industrial%20photography%2C%20shallow%20depth%20of%20field%2C%20high%20detail%20fabric%20weave%20on%20cotton%20glove%2C%20visible%20tiny%20circuit%20components%2C%20fine%20dust%20particles%20in%20blue%20light%2C%20calm%20contemplative%20mood%2C%20shot%20on%20Hasselblad%20medium%20format%20with%2080mm%20lens%20at%20f4%20ISO%20200%2C%20lifelike%20realism&width=2000&height=1200&seq=axiom-studio-step-03-v3-blue&orientation=landscape',
  },
  {
    index: '04',
    dayRange: [10, 11],
    day: 'DAY 10 – 11',
    title: 'Reference tuning',
    body: 'Anechoic room. Off-curve returns to bench.',
    image:
      'https://readdy.ai/api/search-image?query=Ultra%20photorealistic%20hyperreal%20interior%20of%20a%20minimalist%20anechoic%20acoustic%20testing%20chamber%20with%20dark%20charcoal%20blue%20foam%20wedge%20walls%2C%20a%20single%20premium%20audio%20device%20sitting%20on%20a%20matte%20black%20metal%20stand%20in%20the%20center%2C%20calibrated%20measurement%20microphone%20on%20slim%20black%20tripod%2C%20cool%20blue%20accent%20lighting%20glowing%20softly%20from%20behind%20the%20device%2C%20subtle%20cyan%20rim%20light%20on%20the%20foam%20wedge%20tips%2C%20moody%20dark%20editorial%20industrial%20photography%2C%20cinematic%20quiet%20contemplative%20mood%2C%20no%20people%20visible%2C%20high%20detail%20foam%20wedge%20microtexture%2C%20shot%20on%20Hasselblad%20medium%20format%20with%2050mm%20lens%20at%20f4%20ISO%20200%2C%20razor%20sharp%20focus%2C%20lifelike%20realism&width=2000&height=1200&seq=axiom-studio-step-04-v3-blue&orientation=landscape',
  },
];

const TOTAL_DAYS = 11;
const MARKS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11];

function useKanazawaTime() {
  const [t, setT] = useState<string>('--:--:--');
  useEffect(() => {
    const fmt = () => {
      try {
        const now = new Date();
        const parts = new Intl.DateTimeFormat('en-GB', {
          timeZone: 'Asia/Tokyo',
          hour12: false,
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit',
        }).formatToParts(now);
        const h = parts.find((p) => p.type === 'hour')?.value ?? '00';
        const m = parts.find((p) => p.type === 'minute')?.value ?? '00';
        const s = parts.find((p) => p.type === 'second')?.value ?? '00';
        setT(`${h}:${m}:${s}`);
      } catch {
        setT('--:--:--');
      }
    };
    fmt();
    const id = window.setInterval(fmt, 1000);
    return () => window.clearInterval(id);
  }, []);
  return t;
}

function stepForDay(day: number): number {
  for (let i = 0; i < STEPS.length; i++) {
    const [a, b] = STEPS[i].dayRange;
    if (day >= a && day <= b) return i;
  }
  return STEPS.length - 1;
}

export default function StudioSection() {
  const sectionRef = useRef<HTMLElement>(null);
  const trackRef = useRef<HTMLDivElement>(null);
  const [inView, setInView] = useState(false);
  const [day, setDay] = useState<number>(1);
  const [dragging, setDragging] = useState(false);
  const time = useKanazawaTime();

  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) setInView(true);
        });
      },
      { threshold: 0.1 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  // Auto-play: slowly walk through the 11 days while in view & not dragging.
  useEffect(() => {
    if (!inView || dragging) return;
    const id = window.setInterval(() => {
      setDay((d) => (d >= TOTAL_DAYS ? 1 : d + 1));
    }, 2200);
    return () => window.clearInterval(id);
  }, [inView, dragging]);

  const updateFromClientX = (clientX: number) => {
    const el = trackRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const x = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
    // map 0..1 → 1..11
    const d = Math.round(1 + x * (TOTAL_DAYS - 1));
    setDay(d);
  };

  const onTrackPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
    setDragging(true);
    updateFromClientX(e.clientX);
  };
  const onTrackPointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!dragging) return;
    updateFromClientX(e.clientX);
  };
  const onTrackPointerUp = (e: React.PointerEvent<HTMLDivElement>) => {
    (e.target as HTMLElement).releasePointerCapture?.(e.pointerId);
    setDragging(false);
  };

  const activeIdx = stepForDay(day);
  const active = STEPS[activeIdx];
  const percent = ((day - 1) / (TOTAL_DAYS - 1)) * 100;

  const fadeUp = (delay: number, distance = 18) => ({
    opacity: inView ? 1 : 0,
    transform: inView ? 'translateY(0)' : `translateY(${distance}px)`,
    transition: `opacity 900ms ease ${delay}ms, transform 900ms cubic-bezier(0.22,1,0.36,1) ${delay}ms`,
  });

  return (
    <section
      ref={sectionRef}
      id="studio"
      className="relative w-full"
      style={{
        backgroundColor: '#141B2A',
        paddingTop: '120px',
        paddingBottom: '60px',
        fontFamily:
          'Inter, "Helvetica Neue", Helvetica, Arial, system-ui, sans-serif',
        overflow: 'hidden',
      }}
    >
      <div
        className="mx-auto"
        style={{ maxWidth: '1280px', paddingLeft: '56px', paddingRight: '56px' }}
      >
        {/* Header */}
        <div className="grid grid-cols-12 gap-6 items-end mb-10">
          <div className="col-span-12 lg:col-span-6">
            <div
              className="uppercase"
              style={{
                color: '#7AA8EE',
                fontSize: '11px',
                letterSpacing: '0.32em',
                fontWeight: 500,
                marginBottom: '16px',
                ...fadeUp(80, 10),
              }}
            >
              005 — THE STUDIO
            </div>

            <div
              style={{
                fontSize: '140px',
                fontWeight: 700,
                lineHeight: 0.85,
                letterSpacing: '-0.05em',
                color: '#FFFFFF',
                ...fadeUp(200, 22),
              }}
            >
              005
            </div>
          </div>

          <div className="col-span-12 lg:col-span-6 lg:pb-3">
            <h2
              style={{
                color: '#FFFFFF',
                fontSize: '40px',
                fontWeight: 700,
                lineHeight: 1.08,
                letterSpacing: '-0.02em',
                margin: 0,
                marginBottom: '18px',
                ...fadeUp(260, 18),
              }}
            >
              Built by hand
              <br />in Kanazawa.
            </h2>

            <div
              className="flex items-center gap-3"
              style={{
                color: '#FFFFFF',
                fontSize: '18px',
                fontWeight: 500,
                letterSpacing: '0.02em',
                fontFeatureSettings: '"tnum"',
                fontVariantNumeric: 'tabular-nums',
                ...fadeUp(340, 14),
              }}
            >
              <span
                className="inline-block axiom-pulse-dot"
                style={{
                  width: '7px',
                  height: '7px',
                  borderRadius: '50%',
                  background: '#7AA8EE',
                }}
              />
              <span>{time}</span>
            </div>
          </div>
        </div>

        {/* Interactive photo — swaps with the timeline */}
        <div
          className="relative overflow-hidden"
          style={{
            height: '440px',
            marginBottom: '28px',
            ...fadeUp(440, 24),
          }}
        >
          {STEPS.map((s, si) => (
            <img
              key={s.index}
              src={s.image}
              alt={s.title}
              className="absolute inset-0 w-full h-full object-cover object-top pointer-events-none"
              draggable={false}
              style={{
                opacity: si === activeIdx ? 1 : 0,
                transform: si === activeIdx ? 'scale(1.03)' : 'scale(1.08)',
                transition:
                  'opacity 700ms cubic-bezier(0.22,1,0.36,1), transform 1200ms cubic-bezier(0.22,1,0.36,1)',
              }}
            />
          ))}

          <div
            className="absolute inset-0 pointer-events-none"
            style={{
              background:
                'linear-gradient(180deg, rgba(23,27,37,0) 40%, rgba(23,27,37,0.72) 100%)',
            }}
          />

          <div
            className="absolute uppercase flex items-center gap-2"
            style={{
              top: '24px',
              left: '28px',
              color: 'rgba(255,255,255,0.75)',
              fontSize: '10px',
              letterSpacing: '0.32em',
              fontWeight: 500,
            }}
          >
            <span
              className="inline-block axiom-pulse-dot"
              style={{
                width: '6px',
                height: '6px',
                backgroundColor: '#7AA8EE',
                borderRadius: '50%',
              }}
            />
            LIVE · STUDIO 07
          </div>

          <div
            className="absolute uppercase"
            style={{
              top: '24px',
              right: '28px',
              color: 'rgba(255,255,255,0.55)',
              fontSize: '10px',
              letterSpacing: '0.32em',
              fontWeight: 500,
              fontFeatureSettings: '"tnum"',
            }}
          >
            DAY {String(day).padStart(2, '0')} / 11
          </div>

          <div
            className="absolute"
            style={{ left: '28px', bottom: '24px', color: '#FFFFFF' }}
          >
            <div
              className="uppercase"
              style={{
                fontSize: '10px',
                letterSpacing: '0.32em',
                fontWeight: 500,
                color: '#7AA8EE',
                marginBottom: '8px',
              }}
            >
              {active.day}
            </div>
            <div
              key={`title-${active.index}`}
              className="axiom-fade-swap"
              style={{
                fontSize: '26px',
                fontWeight: 700,
                letterSpacing: '-0.015em',
                lineHeight: 1.1,
                marginBottom: '6px',
              }}
            >
              {active.title}
            </div>
            <div
              key={`body-${active.index}`}
              className="axiom-fade-swap"
              style={{
                fontSize: '14px',
                fontWeight: 400,
                color: 'rgba(255,255,255,0.72)',
                letterSpacing: '0.005em',
                maxWidth: '420px',
                lineHeight: 1.65,
              }}
            >
              {active.body}
            </div>
          </div>
        </div>

        {/* Draggable timeline */}
        <div
          className="mb-10"
          style={fadeUp(500, 16)}
        >
          <div
            className="flex items-baseline justify-between mb-4"
            style={{
              color: 'rgba(255,255,255,0.42)',
              fontSize: '10px',
              letterSpacing: '0.32em',
              fontWeight: 500,
              textTransform: 'uppercase',
            }}
          >
            <span>DRAG</span>
            <span
              style={{
                color: '#7AA8EE',
                fontFeatureSettings: '"tnum"',
              }}
            >
              DAY {String(day).padStart(2, '0')} / 11
            </span>
          </div>

          <div
            ref={trackRef}
            onPointerDown={onTrackPointerDown}
            onPointerMove={onTrackPointerMove}
            onPointerUp={onTrackPointerUp}
            onPointerCancel={onTrackPointerUp}
            className="relative axiom-drag-handle select-none"
            style={{
              height: '68px',
              paddingTop: '32px',
              paddingBottom: '4px',
              touchAction: 'none',
            }}
          >
            {/* base rail */}
            <div
              className="absolute left-0 right-0"
              style={{
                top: '43px',
                height: '1px',
                background: 'rgba(255,255,255,0.16)',
              }}
            />
            {/* active portion */}
            <div
              className="absolute left-0"
              style={{
                top: '42px',
                height: '3px',
                width: `${percent}%`,
                background: '#7AA8EE',
                transition: dragging
                  ? 'none'
                  : 'width 320ms cubic-bezier(0.22,1,0.36,1)',
              }}
            />

            {/* Step boundaries (soft ticks under the rail) */}
            {STEPS.map((s, si) => {
              const startPct = ((s.dayRange[0] - 1) / (TOTAL_DAYS - 1)) * 100;
              return (
                <span
                  key={`bound-${si}`}
                  className="absolute"
                  style={{
                    left: `${startPct}%`,
                    top: '38px',
                    width: '1px',
                    height: '11px',
                    background: 'rgba(255,255,255,0.28)',
                    transform: 'translateX(-0.5px)',
                  }}
                />
              );
            })}

            {/* Day marks */}
            {MARKS.map((d) => {
              const pct = ((d - 1) / (TOTAL_DAYS - 1)) * 100;
              const isPast = d <= day;
              return (
                <span
                  key={d}
                  className="absolute"
                  style={{
                    left: `${pct}%`,
                    top: '40px',
                    width: '7px',
                    height: '7px',
                    borderRadius: '50%',
                    background: isPast ? '#7AA8EE' : 'rgba(255,255,255,0.35)',
                    transform: 'translate(-50%, 0)',
                    transition: 'background 220ms ease',
                  }}
                />
              );
            })}

            {/* Handle */}
            <span
              className="absolute"
              style={{
                left: `${percent}%`,
                top: '30px',
                width: '28px',
                height: '28px',
                borderRadius: '50%',
                background: '#7AA8EE',
                boxShadow: dragging
                  ? '0 0 0 8px rgba(122,168,238,0.16)'
                  : '0 0 0 4px rgba(122,168,238,0.08)',
                transform: 'translate(-50%, 0)',
                transition: dragging
                  ? 'left 60ms linear, box-shadow 200ms ease'
                  : 'left 320ms cubic-bezier(0.22,1,0.36,1), box-shadow 200ms ease',
                pointerEvents: 'none',
              }}
            />
            <span
              className="absolute uppercase"
              style={{
                left: `${percent}%`,
                top: '0px',
                transform: 'translateX(-50%)',
                color: '#FFFFFF',
                fontSize: '10px',
                letterSpacing: '0.28em',
                fontWeight: 500,
                background: 'rgba(122,168,238,0.14)',
                border: '1px solid rgba(122,168,238,0.35)',
                padding: '4px 10px',
                borderRadius: '999px',
                pointerEvents: 'none',
                fontFeatureSettings: '"tnum"',
                whiteSpace: 'nowrap',
                transition: dragging
                  ? 'left 60ms linear'
                  : 'left 320ms cubic-bezier(0.22,1,0.36,1)',
              }}
            >
              D{String(day).padStart(2, '0')}
            </span>
          </div>

          <div
            className="flex justify-between mt-3 uppercase"
            style={{
              color: 'rgba(255,255,255,0.3)',
              fontSize: '9.5px',
              letterSpacing: '0.28em',
              fontWeight: 500,
              fontFeatureSettings: '"tnum"',
            }}
          >
            {MARKS.map((d) => (
              <span key={d} style={{ width: '20px', textAlign: 'center' }}>
                D{String(d).padStart(2, '0')}
              </span>
            ))}
          </div>
        </div>

        {/* Interactive 4-column process — highlights based on active day */}
        <div
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-0"
          style={{
            borderTop: '1px solid rgba(255,255,255,0.14)',
          }}
        >
          {STEPS.map((step, idx) => {
            const isActive = idx === activeIdx;
            const isDim = !isActive;
            return (
              <button
                key={step.index}
                type="button"
                onClick={() => setDay(step.dayRange[0])}
                onMouseEnter={() => setDay(step.dayRange[0])}
                className="flex flex-col cursor-pointer relative text-left whitespace-normal group axiom-step-card"
                style={{
                  padding: '24px 24px 28px 24px',
                  paddingLeft: idx === 0 ? '0' : '24px',
                  background: 'transparent',
                  border: 'none',
                  borderRight:
                    idx < STEPS.length - 1
                      ? '1px solid rgba(255,255,255,0.08)'
                      : undefined,
                  opacity: isDim ? 0.55 : 1,
                  transition:
                    'opacity 320ms ease, transform 480ms cubic-bezier(0.22,1,0.36,1)',
                  transform: isActive
                    ? 'translateY(-4px)'
                    : 'translateY(0)',
                  ...fadeUp(600 + idx * 120, 18),
                }}
              >
                <span
                  className="absolute left-0 right-0 top-0 overflow-hidden"
                  style={{
                    height: '2px',
                    background: 'rgba(122,168,238,0.14)',
                    transform: isActive ? 'scaleX(1)' : 'scaleX(0)',
                    transformOrigin: 'left center',
                    transition:
                      'transform 520ms cubic-bezier(0.22,1,0.36,1)',
                  }}
                >
                  <span
                    className="absolute inset-0"
                    style={{
                      background: '#7AA8EE',
                    }}
                  />
                  {isActive && (
                    <span className="absolute top-0 left-0 h-full axiom-shimmer" />
                  )}
                </span>

                <div className="flex items-center justify-between mb-5">
                  <span
                    style={{
                      color: isActive
                        ? '#7AA8EE'
                        : 'rgba(255,255,255,0.7)',
                      fontSize: '42px',
                      fontWeight: 700,
                      lineHeight: 0.9,
                      letterSpacing: '-0.02em',
                      fontFeatureSettings: '"tnum"',
                      transition: 'color 320ms ease, transform 380ms cubic-bezier(0.22,1,0.36,1)',
                      transform: isActive ? 'translateX(2px)' : 'translateX(0)',
                    }}
                    className="group-hover:translate-x-[2px]"
                  >
                    {step.index}
                  </span>
                  <span
                    className="uppercase"
                    style={{
                      color: '#7AA8EE',
                      fontSize: '10px',
                      letterSpacing: '0.28em',
                      fontWeight: 500,
                      paddingTop: '4px',
                    }}
                  >
                    {step.day}
                  </span>
                </div>

                <h4
                  style={{
                    color: '#FFFFFF',
                    fontSize: '18px',
                    fontWeight: 700,
                    letterSpacing: '-0.005em',
                    margin: 0,
                    marginBottom: '8px',
                    transition: 'letter-spacing 400ms ease',
                  }}
                  className="group-hover:tracking-wide"
                >
                  {step.title}
                </h4>
                <p
                  style={{
                    color: '#9AA0AC',
                    fontSize: '13.5px',
                    lineHeight: 1.65,
                    margin: 0,
                  }}
                >
                  {step.body}
                </p>

                {/* micro progress line at the bottom of each card */}
                <span
                  className="absolute left-0 right-6 bottom-3 overflow-hidden"
                  style={{
                    height: '1px',
                    background: 'rgba(255,255,255,0.06)',
                    marginLeft: idx === 0 ? '0' : '24px',
                  }}
                >
                  <span
                    className="block h-full"
                    style={{
                      width: isActive ? '100%' : '0%',
                      background: '#7AA8EE',
                      transition:
                        'width 900ms cubic-bezier(0.22,1,0.36,1)',
                    }}
                  />
                </span>
              </button>
            );
          })}
        </div>
      </div>

    </section>
  );
}